import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import { buildWifiQrPayload, WifiSecurityType } from '@/features/qr/utils/qrUrl';

type QrMode = 'url' | 'wifi';

function normalizeUrl(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

function isValidUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

const QR_OPTIONS = {
  width: 320,
  margin: 2,
  color: {
    dark: '#231c16',
    light: '#ffffff',
  },
} as const;

export const QrCodePage: React.FC = () => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<QrMode>('url');

  const [urlInput, setUrlInput] = useState('');
  const [ssid, setSsid] = useState('');
  const [password, setPassword] = useState('');
  const [security, setSecurity] = useState<WifiSecurityType>('WPA');
  const [hiddenNetwork, setHiddenNetwork] = useState(false);

  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [resultLabel, setResultLabel] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const resetResult = () => {
    setQrDataUrl(null);
    setResultLabel(null);
  };

  const handleModeChange = (nextMode: QrMode) => {
    setMode(nextMode);
    setError(null);
    resetResult();
  };

  const generateFromPayload = async (payload: string, label: string) => {
    setIsGenerating(true);
    setError(null);

    try {
      const dataUrl = await QRCode.toDataURL(payload, QR_OPTIONS);
      setQrDataUrl(dataUrl);
      setResultLabel(label);
    } catch {
      setError(t('qrCode.generationError'));
      resetResult();
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerateUrl = async () => {
    const normalizedUrl = normalizeUrl(urlInput);
    if (!normalizedUrl) {
      setError(t('qrCode.urlRequired'));
      resetResult();
      return;
    }

    if (!isValidUrl(normalizedUrl)) {
      setError(t('qrCode.urlInvalid'));
      resetResult();
      return;
    }

    await generateFromPayload(normalizedUrl, normalizedUrl);
  };

  const handleGenerateWifi = async () => {
    const trimmedSsid = ssid.trim();
    if (!trimmedSsid) {
      setError(t('qrCode.wifi.ssidRequired'));
      resetResult();
      return;
    }

    if (security !== 'nopass' && !password) {
      setError(t('qrCode.wifi.passwordRequired'));
      resetResult();
      return;
    }

    try {
      const payload = buildWifiQrPayload({
        ssid: trimmedSsid,
        password,
        security,
        hidden: hiddenNetwork,
      });
      await generateFromPayload(payload, t('qrCode.wifi.resultLabel', { ssid: trimmedSsid }));
    } catch {
      setError(t('qrCode.generationError'));
      resetResult();
    }
  };

  const handleGenerate = async (event?: FormEvent) => {
    event?.preventDefault();
    if (mode === 'url') {
      await handleGenerateUrl();
    } else {
      await handleGenerateWifi();
    }
  };

  const handleDownload = () => {
    if (!qrDataUrl) return;

    const link = document.createElement('a');
    link.href = qrDataUrl;
    link.download = mode === 'wifi' ? `wifi-${ssid.trim() || 'qrcode'}.png` : 'qrcode.png';
    link.click();
  };

  return (
    <div className="qr-code-page-layout">
      <div className="qr-code-page">
        <h1 className="page-title">{t('qrCode.pageTitle')}</h1>
        <p className="page-description">
          {mode === 'url' ? t('qrCode.description') : t('qrCode.wifi.description')}
        </p>

        <div className="qr-code-tabs" role="tablist" aria-label={t('qrCode.modeLabel')}>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'url'}
            className={`qr-code-tab ${mode === 'url' ? 'active' : ''}`}
            onClick={() => handleModeChange('url')}
          >
            {t('qrCode.modes.url')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'wifi'}
            className={`qr-code-tab ${mode === 'wifi' ? 'active' : ''}`}
            onClick={() => handleModeChange('wifi')}
          >
            {t('qrCode.modes.wifi')}
          </button>
        </div>

        <form className="qr-code-form" onSubmit={handleGenerate}>
          {mode === 'url' ? (
            <div className="form-group">
              <label htmlFor="qr-url">{t('qrCode.urlLabel')}</label>
              <input
                id="qr-url"
                type="url"
                className="qr-code-input"
                value={urlInput}
                onChange={(event) => setUrlInput(event.target.value)}
                placeholder={t('qrCode.urlPlaceholder')}
                autoComplete="url"
              />
            </div>
          ) : (
            <>
              <div className="form-group">
                <label htmlFor="qr-wifi-ssid">{t('qrCode.wifi.ssidLabel')}</label>
                <input
                  id="qr-wifi-ssid"
                  type="text"
                  className="qr-code-input"
                  value={ssid}
                  onChange={(event) => setSsid(event.target.value)}
                  placeholder={t('qrCode.wifi.ssidPlaceholder')}
                  autoComplete="off"
                />
              </div>

              <div className="form-group">
                <label htmlFor="qr-wifi-security">{t('qrCode.wifi.securityLabel')}</label>
                <select
                  id="qr-wifi-security"
                  className="qr-code-input"
                  value={security}
                  onChange={(event) => setSecurity(event.target.value as WifiSecurityType)}
                >
                  <option value="WPA">{t('qrCode.wifi.securityWpa')}</option>
                  <option value="WEP">{t('qrCode.wifi.securityWep')}</option>
                  <option value="nopass">{t('qrCode.wifi.securityNone')}</option>
                </select>
              </div>

              {security !== 'nopass' && (
                <div className="form-group">
                  <label htmlFor="qr-wifi-password">{t('qrCode.wifi.passwordLabel')}</label>
                  <input
                    id="qr-wifi-password"
                    type="text"
                    className="qr-code-input"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder={t('qrCode.wifi.passwordPlaceholder')}
                    autoComplete="off"
                  />
                </div>
              )}

              <div className="form-group form-group-checkbox">
                <label htmlFor="qr-wifi-hidden">
                  <input
                    id="qr-wifi-hidden"
                    type="checkbox"
                    checked={hiddenNetwork}
                    onChange={(event) => setHiddenNetwork(event.target.checked)}
                  />
                  <span>{t('qrCode.wifi.hiddenLabel')}</span>
                </label>
              </div>
            </>
          )}

          {error && <p className="error-message">{error}</p>}

          <button type="submit" className="qr-code-generate-btn" disabled={isGenerating}>
            {isGenerating ? t('qrCode.generating') : t('qrCode.generate')}
          </button>
        </form>

        {qrDataUrl && resultLabel && (
          <div className="qr-code-result">
            <img src={qrDataUrl} alt={t('qrCode.previewAlt')} className="qr-code-image" />
            <p className="qr-code-url">{resultLabel}</p>
            <button type="button" className="qr-code-download-btn" onClick={handleDownload}>
              {t('qrCode.download')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
